/**
 * map_layers.ts — the World Atlas RASTER layers, pure and worker-safe: one
 * function fills one tile's rgba from the world's own fields. The DOM side
 * (lib/map_raster.ts) never samples; the worker (workers/map_raster_worker.ts)
 * calls `sampleTile` and posts the bytes back.
 *
 * TERRAIN: the biome colour lit by height, the sea shaded by depth, the woods
 * tinted toward canopy green — the same look the old in-module sampler drew,
 * with ONE change: `forestDensityMap` (below) instead of `forestDensity`.
 *
 * THE CHEAP FOREST (2026-09-21). `forestDensity` samples the terrain FIVE
 * times per call — the point plus a ±48 m cross for the coast buffer and the
 * slope gate — 190–350 µs a pixel, six times the terrain itself, and both
 * gates are 48 m features: sub-pixel on any map view. The map's tint reads
 * the pixel's OWN height (already computed for the shading), the biome cover,
 * the same two-octave noise against the same quantile threshold, and the same
 * settlement keep-out — everything a stand's outline is made of at map scale.
 * It is NOT the planter's field (that stays the mirrored `forestDensity`);
 * `verify:map-layers` measures how often the two disagree.
 */
import { terrainHeight, biomeAt } from '@shared/terrain.js';
import { forestNoise, forestThreshold, nearestSiteDist, FOREST_COVER, FOREST_SEA_MARGIN_M, FOREST_SITE_KEEP_M, FOREST_SITE_FADE_M, treeLineAt, FOREST_TREE_LINE_FADE_M } from './forest_field.js';
import { sampleWxRow } from './weather.js';
import { sampleConflictRow, type CivSiteSample, type WarSiteSample, type ClashSample } from './conflict_map.js';

export type RasterLayer = 'terrain' | 'temp' | 'weather' | 'humidity' | 'civ' | 'war';

/** Screen pixels per sample, per layer — the cost is per SAMPLE (terrain ~40 µs, the meteo field 2–5×).
 *  Terrain at 5 px matches the old 168-sample underlay's density on a ~1000 px map (the tint, not a photograph). */
export const SAMPLE_PX: Record<RasterLayer, number> = { terrain: 5, temp: 9, weather: 9, humidity: 9, civ: 6, war: 6 };

/** Which layers need a data snapshot beside the seed (the conflict layers read settlement/war rows). */
export const SNAPSHOT_LAYERS: ReadonlySet<RasterLayer> = new Set<RasterLayer>(['civ', 'war']);

export interface ConflictSnapshot { sites: CivSiteSample[]; warSites: WarSiteSample[]; clashes: ClashSample[] }

export interface TileJob {
  id: number;
  layer: RasterLayer;
  seed: string;
  sea: number;
  /** World rect: the tile's min corner and its span (square). */
  x0: number; z0: number; span: number;
  /** Samples per side. */
  n: number;
  /** Time (s) + time-of-day for the weather field; the conflict snapshot id + rows for civ/war. */
  tSec?: number; tod?: number;
  snapId?: number;
  snapshot?: ConflictSnapshot;
}

const BIOME_COLOR: Record<string, [number, number, number]> = {
  regolith_plains: [96, 88, 74], crystal_forest: [88, 74, 116], dune_desert: [128, 108, 70],
  rocky_highlands: [84, 82, 86], moss_wetland: [58, 88, 62], fungal_basin: [60, 92, 92],
};

function smooth(t: number): number { return t * t * (3 - 2 * t); }

/** The map's forest tint at (x, z) given the pixel's own height `h` — see the header. */
export function forestDensityMap(seed: string, x: number, z: number, sea: number, h: number, biome: string): number {
  const cover = FOREST_COVER[biome as keyof typeof FOREST_COVER] ?? 0;
  if (cover <= 0) return 0;
  if (h < sea + FOREST_SEA_MARGIN_M) return 0;
  const n = 0.62 * forestNoise(seed, 'woodsA', x, z, 900) + 0.38 * forestNoise(seed, 'woodsB', x, z, 260);
  const thr = forestThreshold(cover);
  let d = n <= thr - 0.05 ? 0 : n >= thr + 0.09 ? 1 : smooth((n - (thr - 0.05)) / 0.14);
  if (d <= 0) return 0;
  // THE TREE LINE (visual-realism R5, 2026-09-25 — the vegetation agent's insertion): the planter's line
  // (forest_field.treeLineAt) at the pixel's own height, without its exposure term (the slope is a 48 m
  // feature, sub-pixel here) — the map, the far tier's canopy and the woods stop at the same altitude.
  const tl = treeLineAt(seed, x, z, 0);
  if (h >= tl) return 0;
  if (h > tl - FOREST_TREE_LINE_FADE_M) d *= smooth((tl - h) / FOREST_TREE_LINE_FADE_M);
  const ds = nearestSiteDist(seed, x, z);
  if (ds < FOREST_SITE_KEEP_M) return 0;
  if (ds < FOREST_SITE_KEEP_M + FOREST_SITE_FADE_M) d *= (ds - FOREST_SITE_KEEP_M) / FOREST_SITE_FADE_M;
  return d;
}

/** The terrain underlay's colour at (x, z) (the sea by depth, the land by biome × height, the woods tinted). */
export function terrainRgb(seed: string, sea: number, x: number, z: number): [number, number, number] {
  const ht = terrainHeight(seed, x, z);
  if (ht <= sea) {
    const d = Math.min(1, (sea - ht) / 60);
    return [18 + 12 * (1 - d), 42 + 24 * (1 - d), 74 + 34 * (1 - d)];
  }
  const biome = biomeAt(seed, x, z);
  const c = BIOME_COLOR[biome] ?? [90, 90, 90];
  const lum = Math.min(1.45, 0.72 + (ht - sea) / 900);
  const fd = forestDensityMap(seed, x, z, sea, ht, biome);
  const fm = fd > 0.45 ? Math.min(1, (fd - 0.45) * 2.2) * 0.62 : 0;
  return [c[0] * lum * (1 - fm) + 30 * fm, c[1] * lum * (1 - fm) + 96 * fm, c[2] * lum * (1 - fm) + 42 * fm];
}

/** Fill one tile's rgba (n × n × 4, row-major from the tile's min z) for the layer. */
export function sampleTile(job: TileJob, out: Uint8ClampedArray): void {
  const { layer, seed, sea, x0, z0, span, n } = job;
  if (layer === 'terrain') {
    for (let r = 0; r < n; r++) {
      const z = z0 + ((r + 0.5) / n) * span;
      for (let i = 0; i < n; i++) {
        const x = x0 + ((i + 0.5) / n) * span;
        const [cr, cg, cb] = terrainRgb(seed, sea, x, z);
        const o = (r * n + i) * 4;
        out[o] = cr; out[o + 1] = cg; out[o + 2] = cb; out[o + 3] = 255;
      }
    }
    return;
  }
  if (layer === 'civ' || layer === 'war') {
    const s = job.snapshot ?? { sites: [], warSites: [], clashes: [] };
    for (let r = 0; r < n; r++) sampleConflictRow(layer, s.sites, s.warSites, s.clashes, r, n, x0, z0, span, span, out);
    return;
  }
  const tSec = job.tSec ?? 0, tod = job.tod ?? 0.5;
  for (let r = 0; r < n; r++) sampleWxRow(seed, layer, r, n, x0, z0, span, span, tSec, tod, out);
}
